import { ReactNode, useEffect, useState } from 'react'
import { useRouter } from 'next/dist/client/router'
import clsx from 'clsx'
import Link from 'next/link'

import MenuIcon from './MenuIcon'

const Layout = ({ children }: { children: ReactNode }): JSX.Element => {
  const router = useRouter()
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)

  useEffect(() => {
    setIsMobileNavOpen(false)
    document.body.classList.remove('overflow-hidden')
  }, [router.pathname])

  useEffect(() => {
    if (isMobileNavOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }

    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isMobileNavOpen])

  return (
    <div className="w-content max-w-full mt-4 mx-auto px-4">
      <LargeHeader />
      <SmallHeader
        onClickMenuIcon={() => {
          setIsMobileNavOpen((isOpen) => !isOpen)
        }}
      />

      <div className="flex sm:mt-20 border-4 border-teal sm:border-none rounded-xl sm:rounded-none">
        <div className="sm:hidden w-3/12">
          <Sidebar />
        </div>
        <div className="w-9/12 sm:w-full p-6 sm:p-0">{children}</div>
      </div>

      <footer className="my-4 sm:mt-8 py-1 rounded-xl bg-teal text-white text-sm text-center">
        &copy; Robert Karasov 2022
      </footer>

      <MobileNavigation isOpen={isMobileNavOpen} />
    </div>
  )
}

const LargeHeader: React.FC = () => {
  return (
    <>
      <header className="sm:hidden">
        <Link
          className="block py-4 rounded-xl bg-teal text-white text-3xl tracking-wide text-center"
          href="/"
        >
          robert karasov, m.d.
        </Link>
      </header>
      <div className="sm:hidden flex my-2 space-x-2">
        <div className="w-2/12 h-4 rounded-full bg-teal" />
        <div className="w-3/12 h-4 rounded-full bg-blue" />
        <div className="w-1/12 h-4 rounded-full bg-light-blue" />
        <div className="w-6/12 h-4 rounded-full bg-purple" />
      </div>
    </>
  )
}

const SmallHeader: React.FC<{ onClickMenuIcon: () => void }> = ({
  onClickMenuIcon,
}) => {
  return (
    <>
      <header className="hidden sm:flex fixed top-0 left-0 items-center justify-between w-full h-small-header px-4 bg-white border-b border-light-grey z-10">
        <div>
          <Link className="text-teal font-semibold" href="/">
            robert karasov, m.d.
          </Link>
          <div className="flex my-1 space-x-2">
            <div className="w-2/12 h-1 rounded-full bg-teal" />
            <div className="w-3/12 h-1 rounded-full bg-blue" />
            <div className="w-1/12 h-1 rounded-full bg-light-blue" />
            <div className="w-6/12 h-1 rounded-full bg-purple" />
          </div>
        </div>
        <div className="w-6 h-6" onClick={onClickMenuIcon}>
          <MenuIcon />
        </div>
      </header>
    </>
  )
}

const sidebarLinks = [
  {
    href: '/home',
    text: 'home',
  },
  {
    href: '/ceremony',
    text: 'ceremony',
  },
  {
    href: '/need',
    text: 'what you need',
  },
  {
    href: '/tradition',
    text: 'tradition',
  },
  {
    href: '/testimonials',
    text: 'testimonials',
  },
  {
    href: '/bris',
    text: 'why have a bris',
  },
  {
    href: '/interfaith',
    text: 'interfaith couples',
  },
  {
    href: '/after',
    text: 'after the bris',
  },
  {
    href: '/contact',
    text: 'contact',
  },
]

const Sidebar: React.FC = () => {
  const router = useRouter()

  return (
    <div>
      <nav>
        <ul className="space-y-1">
          {sidebarLinks.map(({ href, text }, i) => {
            const isActiveLink = href === router.pathname

            return (
              <li key={text}>
                <Link
                  className={clsx(
                    'block py-4 px-2 text-white text-xl text-right',
                    {
                      'bg-blue': isActiveLink,
                      'bg-teal': !isActiveLink,
                      // These roundings are for the first and last sidebar links when they're active
                      // since the active background color is different than the border color and without
                      // the rounding the link juts into the border.
                      'rounded-tl-md': isActiveLink && i === 0,
                      'rounded-bl-md':
                        isActiveLink && i === sidebarLinks.length - 1,
                    }
                  )}
                  href={href}
                >
                  {text}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </div>
  )
}

const MobileNavigation: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const router = useRouter()

  return (
    <div
      className={clsx(
        'none sm:block fixed top-small-header right-0 bottom-0 left-0 w-full transition px-4 bg-white',
        {
          'opacity-1 visible': isOpen,
          'opacity-0 invisible': !isOpen,
        }
      )}
    >
      <nav>
        <ul className="space-y-1">
          {sidebarLinks.map(({ href, text }) => {
            const isActiveLink = href === router.pathname

            return (
              <li key={text}>
                <Link
                  className={clsx(
                    'block py-2 border-b border-light-grey text-xl',
                    {
                      'text-teal': isActiveLink,
                    }
                  )}
                  href={href}
                >
                  {text}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </div>
  )
}

export default Layout
